import ManualLink from "../base/ManualLink";
import { MANUAL_ENTRY } from "../../data/Manual";
import * as React from "react";
import CustomerPrograms from "./CustomerPrograms";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import SaveIcon from "@mui/icons-material/Save";
import TuneIcon from "@mui/icons-material/Tune";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  GAME_TO_REAL_YEARS,
  ORGANIC_GROWTH_MAX_ANNUAL,
  TICK_MINUTES,
  TICKS_PER_YEAR,
} from "../../Constants";
import {
  DerivedHistoryKeysType,
  FacilityOperatingType,
  FuelNameType,
  GameType,
  GeneratorOperatingType,
  MonthlyHistoryType,
  TickPresentFutureType,
  UnitSystemType,
} from "../../Types";
import {
  deriveExpandedSummary,
  EMPTY_HISTORY,
  getDateFromMinute,
  getTimeFromTimeline,
  MINUTES_PER_MONTH,
  reduceHistories,
  summarizeTimeline,
  summarizeTimelineByMonth,
} from "../../helpers/DateTime";
import {
  customerMarketSizeAt,
  getMarketRate,
  projectCustomerChange,
} from "../../helpers/Customers";
import { getDispatchOrderedFuels } from "../../helpers/Energy";
import { facilityLifetime } from "../../helpers/Financials";
import {
  formatMoneyConcise,
  formatWattHours,
  formatWatts,
} from "../../helpers/Format";
import {
  formatLargeMassValueConcise,
  largeMassUnit,
} from "../../helpers/Units";
import {
  getStorageJson,
  getStorageString,
  setStorageKeyValue,
} from "../../LocalStorage";
import { generateNewTimeline } from "../../reducers/Game";
import { getScenario, SCENARIOS } from "../../data/Scenarios";
import {
  chartPalette,
  demandTypeColors,
  fuelColors,
  fuelDashArrays,
} from "../../Theme";
import ChartForecastDemandByType, {
  demandTypesBySizeAtStart,
} from "../base/ChartForecastDemandByType";
import ChartFinances from "../base/ChartFinances";
import ChartForecastFuelPrices, {
  PRICED_FUELS,
} from "../base/ChartForecastFuelPrices";
import ChartForecastRenewableCapacityFactor from "../base/ChartForecastRenewableCapacityFactor";
import ChartForecastStorage from "../base/ChartForecastStorage";
import ChartForecastSupplyByFuel, {
  forecastFuels,
} from "../base/ChartForecastSupplyByFuel";
import ChartForecastSupplyDemand from "../base/ChartForecastSupplyDemand";
import ChartForecastWater from "../base/ChartForecastWater";
import ChartForecastWeather from "../base/ChartForecastWeather";
import ChartLegend from "../base/ChartLegend";
import GameCard from "../base/GameCard";
import ForecastScope from "../base/ForecastScope";
import EconomicFutureComparison from "../base/EconomicFutureComparison";
import { forecastShortfalls } from "../../helpers/ForecastShortfalls";
import { UnitsContext } from "../base/UnitsContext";
import { buildChartKeys, formatCustomerChange } from "./Finances";
import { sampleForecastTimeline } from "../../helpers/ForecastSampling";
import { ChartAnnotationsContext } from "../base/ChartAnnotationsContext";
import InsightEventRail from "../base/InsightEventRail";
import { UpcomingStoryEventType } from "./StoryEventSelectors";
import {
  ChartViewportContext,
  ChartViewportRange,
  clampChartViewport,
  eventChartViewport,
  panChartViewport,
  rangesEqual,
  zoomChartViewport,
} from "../base/ChartViewportContext";
import PowerExchangeSummary from "../base/PowerExchangeSummary";
import { transmissionAvailable } from "../../data/AdjacentMarkets";

export type InsightLayerId =
  | "supplyDemand"
  | "powerExchange"
  | "demandByType"
  | "supplyByFuel"
  | "storage"
  | "fuelPrices"
  | "solarCapacityFactor"
  | "water"
  | "weather"
  | "profit"
  | "revenue"
  | "expenses"
  | "cash"
  | "customers"
  | "emissions"
  | "financeDetails"
  | "inflationInterest";

type LayerGroup = "Grid" | "Customers" | "Economics" | "Environment";

export interface InsightLayerDefinition {
  id: InsightLayerId;
  label: string;
  group: LayerGroup;
  availability?: "storage" | "hydro" | "transmission";
}

export const INSIGHT_LAYERS: readonly InsightLayerDefinition[] = [
  { id: "supplyDemand", label: "Supply & Demand", group: "Grid" },
  {
    id: "powerExchange",
    label: "Power exchange",
    group: "Grid",
    availability: "transmission",
  },
  {
    id: "demandByType",
    label: "Demand by use",
    group: "Customers",
  },
  { id: "supplyByFuel", label: "Supply by Fuel", group: "Grid" },
  {
    id: "storage",
    label: "Stored Energy",
    group: "Grid",
    availability: "storage",
  },
  { id: "customers", label: "Customers", group: "Customers" },
  { id: "profit", label: "Profit", group: "Economics" },
  { id: "revenue", label: "Revenue", group: "Economics" },
  { id: "expenses", label: "Expenses", group: "Economics" },
  { id: "cash", label: "Cash", group: "Economics" },
  { id: "financeDetails", label: "Finance details", group: "Economics" },
  { id: "fuelPrices", label: "Fuel Prices", group: "Economics" },
  {
    id: "inflationInterest",
    label: "Inflation & interest rate",
    group: "Economics",
  },
  {
    id: "emissions",
    label: "Emissions (CO2e)",
    group: "Environment",
  },
  {
    id: "solarCapacityFactor",
    label: "Renewable output",
    group: "Environment",
  },
  { id: "weather", label: "Temperature", group: "Environment" },
  { id: "water", label: "Water", group: "Environment", availability: "hydro" },
] as const;

export type DefaultInsightPresetId =
  "overview" | "reliability" | "profitability" | "growth" | "decarbonization";

export type InsightPresetId =
  DefaultInsightPresetId | "custom" | `saved:${string}`;

export interface CustomInsightPreset {
  id: string;
  name: string;
  layers: InsightLayerId[];
}

interface InsightPresetLibrary {
  defaults: Partial<Record<DefaultInsightPresetId, InsightLayerId[]>>;
  custom: CustomInsightPreset[];
}

export const INSIGHT_PRESETS: Record<
  DefaultInsightPresetId,
  { label: string; layers: InsightLayerId[] }
> = {
  // Each preset reads from the outcome a player is trying to protect into the causes they can
  // act on. Overview is deliberately the five universal health signals: optional technologies
  // belong in the diagnostic presets, not in the first view a new player sees.
  overview: {
    label: "Overview",
    layers: ["supplyDemand", "cash", "profit", "customers", "emissions"],
  },
  reliability: {
    label: "Reliability",
    layers: [
      "supplyDemand",
      "powerExchange",
      "supplyByFuel",
      "storage",
      "weather",
      "water",
    ],
  },
  profitability: {
    label: "Profitability",
    layers: ["profit", "cash", "revenue", "expenses", "fuelPrices"],
  },
  growth: {
    label: "Growth & pricing",
    layers: ["customers", "demandByType", "supplyDemand", "revenue", "profit"],
  },
  decarbonization: {
    label: "Cutting emissions",
    layers: [
      "emissions",
      "supplyByFuel",
      "supplyDemand",
      "fuelPrices",
      "profit",
    ],
  },
};

const LAYERS_KEY = "insightsLayers";
const ACTIVE_PRESET_KEY = "insightsActivePreset";
const PRESET_LIBRARY_KEY = "insightsPresetLibrary";
export const MAX_CUSTOM_INSIGHT_PRESETS = 10;
const MAX_PRESET_NAME_LENGTH = 40;
const SYNC_KEY = "insights";
const GROUPS: LayerGroup[] = ["Grid", "Customers", "Economics", "Environment"];
const ALL_LAYER_IDS = new Set(INSIGHT_LAYERS.map((layer) => layer.id));
const HOURS_PER_RECORDED_MONTH = 24 * GAME_TO_REAL_YEARS;

function formatRateCompact(rate: number): string {
  return `${Number((rate * 100).toFixed(1))}¢`;
}

function rateMarkLabel(
  rate: number,
  desktopLabel = formatMoneyConcise(rate),
  mobileLabel = "",
) {
  return (
    <>
      <span className="insightsRateMarkDesktop">{desktopLabel}</span>
      <span className="insightsRateMarkMobile">{mobileLabel}</span>
    </>
  );
}

const RATE_MARKS = [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3].map((rate) => ({
  value: rate,
  label: rateMarkLabel(
    rate,
    formatMoneyConcise(rate),
    [0, 0.15, 0.3].includes(rate) ? formatRateCompact(rate) : "",
  ),
}));

interface BlackoutEdges {
  minute: number;
  value: number;
}

interface ProjectionView {
  timeline: TickPresentFutureType[];
  sampled: TickPresentFutureType[];
  supplyDemandTimeline: TickPresentFutureType[];
  domain: { x: [number, number]; y: [number, number] };
  blackouts: BlackoutEdges[];
  blackoutTotalWh: number;
  largestBlackout: { wh: number; peakW: number; start: number; end: number };
  hasStorage: boolean;
  hasHydro: boolean;
  financePast: MonthlyHistoryType[];
  financeProjected: MonthlyHistoryType[];
  projectionStepMinutes: number;
}

export interface StateProps {
  journeyRestore?: import("../../Types").InsightsOriginType;
  configurationRevision?: number;
  evidenceRequest?: import("../../Types").EvidenceRequestType;
  evidenceRunId?: number;
  activeCard?: import("../../Types").CardNameType;
  game: GameType;
  selectedFacilityId: number | null;
  facilityDragActive: boolean;
  focusLayer?: InsightLayerId;
  upcomingEvents?: UpcomingStoryEventType[];
}

export interface DispatchProps {
  onGeneratorJourney?: (
    origin: import("../../Types").InsightsOriginType,
  ) => void;
  onConfigurationEdit?: () => void;
  onJourneyRestored?: (
    origin: import("../../Types").InsightsOriginType,
  ) => boolean;
  onEvidenceReady?: (
    request: import("../../Types").EvidenceRequestType,
    element: HTMLElement | null,
  ) => void;
  onDelta: (delta: Partial<GameType>) => void;
}

export interface Props extends StateProps, DispatchProps {}

interface State {
  temporaryLayer?: InsightLayerId;
  layers: InsightLayerId[];
  preset: InsightPresetId;
  presetDirty: boolean;
  presetLibrary: InsightPresetLibrary;
  presetMenuAnchor: HTMLElement | null;
  presetDialog: "saveAs" | "rename" | "delete" | "restore" | null;
  presetName: string;
  presetNameError: string;
  layersOpen: boolean;
  leversOpen: boolean;
  activeEventKey?: string;
  viewport: ChartViewportRange;
  viewportAnnouncement: string;
}

const VIEWPORT_ZOOM_FACTOR = 0.5;
const VIEWPORT_PAN_FRACTION = 0.25;
const DEFAULT_VIEWPORT_MONTHS = 12;
const MAX_FORECAST_YEARS = 20;

function viewportBounds(game: GameType): ChartViewportRange {
  return [0, game.date.minute + MAX_FORECAST_YEARS * 12 * MINUTES_PER_MONTH];
}

function initialViewport(game: GameType): ChartViewportRange {
  const bounds = viewportBounds(game);
  return clampChartViewport(
    bounds,
    [
      game.date.minute,
      game.date.minute + DEFAULT_VIEWPORT_MONTHS * MINUTES_PER_MONTH,
    ],
    MINUTES_PER_MONTH,
  );
}

function viewportLabel(
  range: ChartViewportRange,
  startingYear: number,
): string {
  const start = getDateFromMinute(range[0], startingYear);
  const end = getDateFromMinute(range[1], startingYear);
  if (start.year !== end.year) {
    const sameCentury =
      Math.floor(start.year / 100) === Math.floor(end.year / 100);
    const endYear = sameCentury ? String(end.year).slice(-2) : end.year;
    return `${start.year}–${endYear}`;
  }
  if (start.month === end.month) return `${start.month} ${start.year}`;
  return `${start.month}–${end.month} ${start.year}`;
}

function viewportAnnouncement(
  range: ChartViewportRange,
  startingYear: number,
): string {
  return `Showing ${viewportLabel(range, startingYear)}`;
}

function timelineWithin(
  timeline: TickPresentFutureType[],
  range: ChartViewportRange,
): TickPresentFutureType[] {
  if (timeline.length <= 2) return timeline;
  let first = 0;
  let last = timeline.length;
  while (first < last) {
    const middle = Math.floor((first + last) / 2);
    if (timeline[middle].minute < range[0]) first = middle + 1;
    else last = middle;
  }
  const from = Math.max(0, first - 1);
  first = from;
  last = timeline.length;
  while (first < last) {
    const middle = Math.floor((first + last) / 2);
    if (timeline[middle].minute <= range[1]) first = middle + 1;
    else last = middle;
  }
  return timeline.slice(from, Math.min(timeline.length, first + 1));
}

function sameLayers(left: InsightLayerId[], right: InsightLayerId[]): boolean {
  return (
    left.length === right.length &&
    left.every((layer, index) => layer === right[index])
  );
}

function validLayers(value: unknown): InsightLayerId[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (id, index): id is InsightLayerId =>
      typeof id === "string" &&
      ALL_LAYER_IDS.has(id as InsightLayerId) &&
      value.indexOf(id) === index,
  );
}

function storedPresetLibrary(): InsightPresetLibrary {
  const stored = getStorageJson<{
    defaults?: Partial<Record<DefaultInsightPresetId, unknown>>;
    custom?: unknown[];
  }>(PRESET_LIBRARY_KEY, {});
  const defaults: InsightPresetLibrary["defaults"] = {};
  for (const id of Object.keys(INSIGHT_PRESETS) as DefaultInsightPresetId[]) {
    const layers = validLayers(stored.defaults?.[id]);
    if (layers.length) {
      defaults[id] = layers;
    }
  }

  const names = new Set<string>();
  const ids = new Set<string>();
  const custom: CustomInsightPreset[] = [];
  for (const value of stored.custom || []) {
    if (!value || typeof value !== "object") {
      continue;
    }
    const candidate = value as Partial<CustomInsightPreset>;
    const id = typeof candidate.id === "string" ? candidate.id : "";
    const name =
      typeof candidate.name === "string" ? candidate.name.trim() : "";
    const layers = validLayers(candidate.layers);
    const normalizedName = name.toLocaleLowerCase();
    if (
      !id ||
      !name ||
      name.length > MAX_PRESET_NAME_LENGTH ||
      !layers.length ||
      ids.has(id) ||
      names.has(normalizedName)
    ) {
      continue;
    }
    ids.add(id);
    names.add(normalizedName);
    custom.push({ id, name, layers });
    if (custom.length === MAX_CUSTOM_INSIGHT_PRESETS) {
      break;
    }
  }
  return { defaults, custom };
}

function presetDefinition(
  id: InsightPresetId,
  library: InsightPresetLibrary,
): { label: string; layers: InsightLayerId[] } | undefined {
  if (id === "custom") {
    return undefined;
  }
  if (id.startsWith("saved:")) {
    const saved = library.custom.find(
      (preset) => preset.id === id.slice("saved:".length),
    );
    return saved ? { label: saved.name, layers: saved.layers } : undefined;
  }
  const defaultPreset = INSIGHT_PRESETS[id as DefaultInsightPresetId];
  return defaultPreset
    ? {
        label: defaultPreset.label,
        layers:
          library.defaults[id as DefaultInsightPresetId] ||
          defaultPreset.layers,
      }
    : undefined;
}

function matchingPreset(
  layers: InsightLayerId[],
  library: InsightPresetLibrary,
): InsightPresetId {
  for (const id of Object.keys(INSIGHT_PRESETS) as DefaultInsightPresetId[]) {
    if (sameLayers(layers, presetDefinition(id, library)!.layers)) {
      return id;
    }
  }
  const custom = library.custom.find((preset) =>
    sameLayers(layers, preset.layers),
  );
  return custom ? `saved:${custom.id}` : "custom";
}

function isStoredPreset(
  id: string,
  library: InsightPresetLibrary,
): id is InsightPresetId {
  return !!presetDefinition(id as InsightPresetId, library);
}

function nextCustomPresetId(custom: CustomInsightPreset[]): string {
  return String(
    custom.reduce((highest, preset) => {
      const numericId = Number(preset.id);
      return Number.isInteger(numericId)
        ? Math.max(highest, numericId)
        : highest;
    }, 0) + 1,
  );
}

function storedLayers(): InsightLayerId[] {
  const valid = validLayers(getStorageJson<string[]>(LAYERS_KEY, []));
  return valid.length ? valid : [...INSIGHT_PRESETS.overview.layers];
}

export function presetForLayers(layers: InsightLayerId[]): InsightPresetId {
  const match = Object.entries(INSIGHT_PRESETS).find(
    ([, preset]) =>
      preset.layers.length === layers.length &&
      preset.layers.every((layer, index) => layer === layers[index]),
  );
  return (match?.[0] as InsightPresetId | undefined) || "custom";
}

function requiredTutorialLayers(scenarioId: number): InsightLayerId[] {
  switch (scenarioId) {
    case 4:
      return ["profit", "financeDetails"];
    case 3:
      return ["customers"];
    case 5:
      return ["supplyDemand", "fuelPrices", "weather"];
    case 112:
      return ["powerExchange"];
    default:
      return [];
  }
}

export function withRequiredLayers(
  layers: InsightLayerId[],
  scenarioId: number,
): InsightLayerId[] {
  if (scenarioId === 112) {
    return ["powerExchange", ...layers.filter((id) => id !== "powerExchange")];
  }
  const next = [...layers];
  for (const id of requiredTutorialLayers(scenarioId)) {
    if (!next.includes(id)) {
      next.push(id);
    }
  }
  return next;
}

function monthMinute(month: MonthlyHistoryType, startingYear: number): number {
  return (
    ((month.year - startingYear) * 12 + month.month - 1) * MINUTES_PER_MONTH
  );
}

function financeMetadata(
  id: InsightLayerId,
  units: UnitSystemType,
): {
  key: DerivedHistoryKeysType;
  label: string;
  format: (value: number) => string;
} | null {
  switch (id) {
    case "profit":
      return { key: "profit", label: "Profit", format: formatMoneyConcise };
    case "revenue":
      return { key: "revenue", label: "Revenue", format: formatMoneyConcise };
    case "expenses":
      return { key: "expenses", label: "Expenses", format: formatMoneyConcise };
    case "cash":
      return { key: "cash", label: "Cash", format: formatMoneyConcise };
    case "customers":
      return {
        key: "customers",
        label: "Customers",
        format: (value) =>
          new Intl.NumberFormat(undefined, { notation: "compact" }).format(
            value,
          ),
      };
    case "emissions":
      return {
        key: "kgco2e",
        label: `CO2e Emitted (${largeMassUnit(units)})`,
        format: (value) => formatLargeMassValueConcise(value, units),
      };
    default:
      return null;
  }
}

function financeSeries(
  key: DerivedHistoryKeysType,
  past: MonthlyHistoryType[],
  projected: MonthlyHistoryType[],
  domain?: ChartViewportRange,
  startingYear?: number,
) {
  const point = (month: MonthlyHistoryType, isProjected: boolean) => {
    const summary = deriveExpandedSummary(month);
    return {
      month: summary.year * 12 + summary.month,
      year: summary.year,
      value: summary[key],
      projected: isProjected,
    };
  };
  const points = [
    ...[...past].reverse().map((month) => point(month, false)),
    ...projected.map((month) => point(month, true)),
  ];
  if (!domain || startingYear === undefined || points.length <= 2)
    return points;
  const minutes = points.map(
    (value) => (value.month - startingYear * 12 - 1) * MINUTES_PER_MONTH,
  );
  const firstInside = minutes.findIndex((minute) => minute >= domain[0]);
  const first =
    firstInside < 0 ? points.length - 1 : Math.max(0, firstInside - 1);
  const firstAfter = minutes.findIndex((minute) => minute > domain[1]);
  const end = firstAfter < 0 ? points.length : firstAfter + 1;
  return points.slice(first, Math.max(first + 1, Math.min(points.length, end)));
}

function policySignature(game: GameType): string {
  const policies = game.policies;
  if (!policies) return "";
  return JSON.stringify([
    policies.month,
    ...Object.keys(policies.programs)
      .sort()
      .map((id) => {
        const program = policies.programs[id as keyof typeof policies.programs];
        return [
          id,
          program.tier,
          program.adoption,
          program.spending,
          program.pending?.tier,
          program.startHour,
          program.pending?.startHour,
          program.pending?.month,
        ];
      }),
  ]);
}

function facilitySignature(game: GameType): string {
  const facilities = game.facilities
    .map((facility) =>
      [
        facility.id,
        facility.paused,
        facility.yearsToBuildLeft,
        facility.peakW,
      ].join(":"),
    )
    .join("|");
  const transmission = (game.transmission?.lines || [])
    .map((line) => [line.id, line.corridorId, line.yearsToBuildLeft].join(":"))
    .join("|");
  return `${facilities}/${game.transmission?.tradingPolicy || "BALANCED"}/${transmission}`;
}

export default class Insights extends React.Component<Props, State> {
  static contextType = UnitsContext;

  private projectionCache:
    { key: string; projection: ProjectionView } | undefined;

  constructor(props: Props) {
    super(props);
    const presetLibrary = storedPresetLibrary();
    const layers = storedLayers();
    const storedPreset = getStorageString(ACTIVE_PRESET_KEY, "");
    const preset = isStoredPreset(storedPreset, presetLibrary)
      ? storedPreset
      : matchingPreset(layers, presetLibrary);
    const savedLayers = presetDefinition(preset, presetLibrary)?.layers;
    this.state = {
      temporaryLayer: props.focusLayer,
      layers,
      preset,
      presetDirty: !savedLayers || !sameLayers(layers, savedLayers),
      presetLibrary,
      presetMenuAnchor: null,
      presetDialog: null,
      presetName: "",
      presetNameError: "",
      layersOpen: false,
      leversOpen: true,
      activeEventKey: undefined,
      viewport: props.journeyRestore
        ? this.restoredViewport(props.journeyRestore)
        : initialViewport(props.game),
      viewportAnnouncement: "",
    };
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State) {
    // Projection generation simulates as much as twenty years and then redraws every visible
    // chart. Doing that in the middle of a pointer-driven fleet reorder is the periodic desktop
    // hitch users feel most. Keep the last projection for the few hundred milliseconds of the
    // drag, then catch up once on release.
    if (nextProps.facilityDragActive) {
      return false;
    }
    if (this.props.facilityDragActive) {
      return true;
    }
    return (
      nextState !== this.state ||
      nextProps.journeyRestore !== this.props.journeyRestore ||
      nextProps.evidenceRequest !== this.props.evidenceRequest ||
      nextProps.evidenceRunId !== this.props.evidenceRunId ||
      nextProps.activeCard !== this.props.activeCard ||
      nextProps.game.tutorialStep !== this.props.game.tutorialStep ||
      nextProps.game.date.monthsElapsed !==
        this.props.game.date.monthsElapsed ||
      (this.state.layers.includes("powerExchange") &&
        nextProps.game.date.minute !== this.props.game.date.minute) ||
      nextProps.game.dollarsPerkWh !== this.props.game.dollarsPerkWh ||
      nextProps.game.feePerKgCO2e !== this.props.game.feePerKgCO2e ||
      nextProps.selectedFacilityId !== this.props.selectedFacilityId ||
      nextProps.focusLayer !== this.props.focusLayer ||
      nextProps.upcomingEvents !== this.props.upcomingEvents ||
      policySignature(nextProps.game) !== policySignature(this.props.game) ||
      facilitySignature(nextProps.game) !== facilitySignature(this.props.game)
    );
  }

  public componentDidMount() {
    this.scrollTutorialPowerExchangeIntoView();
    this.resolveEvidence();
    this.restoreJourney();
  }

  public componentDidUpdate(previousProps: Props) {
    if (
      this.props.evidenceRunId !== previousProps.evidenceRunId ||
      (this.props.activeCard !== previousProps.activeCard &&
        !this.props.evidenceRequest)
    ) {
      if (this.state.temporaryLayer)
        this.setState({ temporaryLayer: undefined });
    }
    this.resolveEvidence();
    if (this.props.journeyRestore) this.restoreJourney();
    if (this.props.game.tutorialStep !== previousProps.game.tutorialStep) {
      this.scrollTutorialPowerExchangeIntoView();
    }
    if (
      this.props.game.date.monthsElapsed !==
      previousProps.game.date.monthsElapsed
    ) {
      const elapsedMonths =
        this.props.game.date.monthsElapsed -
        previousProps.game.date.monthsElapsed;
      const delta = elapsedMonths * MINUTES_PER_MONTH;
      const bounds = viewportBounds(this.props.game);
      this.setState((state) => {
        const anchoredAtScenarioStart = state.viewport[0] === bounds[0];
        const viewport = clampChartViewport(
          bounds,
          [
            anchoredAtScenarioStart ? bounds[0] : state.viewport[0] + delta,
            state.viewport[1] + delta,
          ],
          MINUTES_PER_MONTH,
        );
        return {
          viewport,
          viewportAnnouncement: viewportAnnouncement(
            viewport,
            this.props.game.startingYear,
          ),
        };
      });
    }
    if (
      this.props.focusLayer &&
      this.props.focusLayer !== previousProps.focusLayer &&
      !this.state.layers.includes(this.props.focusLayer)
    ) {
      this.setState({ temporaryLayer: this.props.focusLayer });
    }
  }

  private restoredViewport(
    origin: import("../../Types").InsightsOriginType,
  ): ChartViewportRange {
    const bounds = viewportBounds(this.props.game);
    const delta =
      (this.props.game.date.monthsElapsed - origin.month) * MINUTES_PER_MONTH;
    return clampChartViewport(
      bounds,
      [
        origin.viewport[0] === 0 ? 0 : origin.viewport[0] + delta,
        origin.viewport[1] + delta,
      ],
      MINUTES_PER_MONTH,
    );
  }

  private beginJourney(sourceLayer?: InsightLayerId) {
    const pane = document.querySelector<HTMLElement>(".insights .scrollable");
    const anchor = Array.from(
      document.querySelectorAll<HTMLElement>(".insights [data-layer]"),
    ).find(
      (element) =>
        element.getBoundingClientRect().bottom >
        (pane?.getBoundingClientRect().top ?? 0),
    );
    this.props.onGeneratorJourney?.({
      viewport: [...this.state.viewport],
      month: this.props.game.date.monthsElapsed,
      layers: [...this.state.layers],
      preset: this.state.preset,
      revision: this.props.configurationRevision ?? 0,
      temporaryLayer: this.state.temporaryLayer,
      anchor: sourceLayer ?? anchor?.dataset.layer,
      scrollTop: pane?.scrollTop ?? 0,
    });
  }

  private restoreJourney() {
    const origin = this.props.journeyRestore;
    if (!origin || this.props.facilityDragActive) return;
    if (this.props.onJourneyRestored && !this.props.onJourneyRestored(origin))
      return;
    const unchanged =
      origin.revision === (this.props.configurationRevision ?? 0);
    const viewport = this.restoredViewport(origin);
    this.setState(
      {
        viewport,
        viewportAnnouncement: viewportAnnouncement(
          viewport,
          this.props.game.startingYear,
        ),
        layers: unchanged
          ? (origin.layers as InsightLayerId[])
          : this.state.layers,
        preset: unchanged
          ? (origin.preset as InsightPresetId)
          : this.state.preset,
        temporaryLayer: (unchanged ? origin.temporaryLayer : origin.anchor) as
          InsightLayerId | undefined,
      },
      () => {
        const pane = document.querySelector<HTMLElement>(
          ".insights .scrollable",
        );
        if (pane) pane.scrollTop = origin.scrollTop;
        const anchor = document.querySelector<HTMLElement>(
          origin.anchor
            ? '.insights [data-layer="' + origin.anchor + '"]'
            : "#insightsGeneratorJourney",
        );
        anchor?.focus({ preventScroll: true });
      },
    );
  }

  private resolveEvidence() {
    const request = this.props.evidenceRequest;
    if (!request || this.props.facilityDragActive) return;
    const target = request.target;
    let layer: InsightLayerId | undefined;
    if (target === "finances") layer = "financeDetails";
    else if (typeof target === "object" && target.card === "INSIGHTS") {
      layer =
        target.layer === "FINANCES"
          ? "financeDetails"
          : target.layer === "FUEL_PRICES"
            ? "fuelPrices"
            : "supplyDemand";
    }
    if (!layer) return;
    if (
      !this.state.layers.includes(layer) &&
      this.state.temporaryLayer !== layer
    ) {
      this.setState({ temporaryLayer: layer });
      return;
    }
    this.props.onEvidenceReady?.(
      request,
      document.querySelector<HTMLElement>(`[data-layer="${layer}"]`),
    );
  }

  private scrollTutorialPowerExchangeIntoView() {
    if (
      this.props.game.scenarioId !== 112 ||
      this.props.game.tutorialStep !== 7 ||
      window.innerWidth > 768
    ) {
      return;
    }
    window.setTimeout(() => {
      document
        .querySelector<HTMLElement>('[data-layer="powerExchange"]')
        ?.scrollIntoView?.({ block: "nearest" });
    }, 0);
  }

  private setLayers(
    layers: InsightLayerId[],
    preset: InsightPresetId = this.state.preset,
  ) {
    this.props.onConfigurationEdit?.();
    const required = layers;
    const savedLayers = presetDefinition(
      preset,
      this.state.presetLibrary,
    )?.layers;
    // Mission 7 temporarily puts its teaching track first. Keep that guided ordering out of the
    // player's saved preset so finishing the mission does not rearrange their normal Insights.
    if (this.props.game.scenarioId !== 112) {
      setStorageKeyValue(LAYERS_KEY, required);
      setStorageKeyValue(ACTIVE_PRESET_KEY, preset);
    }
    this.setState({
      temporaryLayer: undefined,
      layers: required,
      preset,
      presetDirty: !savedLayers || !sameLayers(required, savedLayers),
    });
  }

  private toggleLayer(id: InsightLayerId) {
    const required = requiredTutorialLayers(this.props.game.scenarioId);
    if (this.state.layers.includes(id)) {
      if (!required.includes(id)) {
        this.setLayers(this.state.layers.filter((layer) => layer !== id));
      }
    } else {
      this.setLayers([...this.state.layers, id]);
    }
  }

  private applyPreset(id: Exclude<InsightPresetId, "custom">) {
    const preset = presetDefinition(id, this.state.presetLibrary);
    if (!preset) {
      return;
    }
    this.props.onConfigurationEdit?.();
    const layers = [...preset.layers];
    if (this.props.game.scenarioId !== 112) {
      setStorageKeyValue(LAYERS_KEY, layers);
      setStorageKeyValue(ACTIVE_PRESET_KEY, id);
    }
    this.setState({
      layers,
      preset: id,
      presetDirty: false,
      temporaryLayer: undefined,
    });
  }

  private moveLayer(id: InsightLayerId, neighbour: InsightLayerId) {
    const layers = [...this.state.layers];
    const from = layers.indexOf(id);
    const to = layers.indexOf(neighbour);
    if (from < 0 || to < 0) {
      // An explicit edit ends temporary evidence, but never adds an unconfigured chart.
      this.setLayers(layers);
      return;
    }
    [layers[from], layers[to]] = [layers[to], layers[from]];
    this.setLayers(layers);
  }

  private savePresetLibrary(library: InsightPresetLibrary) {
    setStorageKeyValue(PRESET_LIBRARY_KEY, library);
  }

  private selectedCustomPreset(): CustomInsightPreset | undefined {
    if (!this.state.preset.startsWith("saved:")) {
      return undefined;
    }
    return this.state.presetLibrary.custom.find(
      (preset) => preset.id === this.state.preset.slice("saved:".length),
    );
  }

  private savePresetChanges() {
    const { layers, preset, presetLibrary } = this.state;
    if (!layers.length || preset === "custom") {
      this.openPresetDialog("saveAs");
      return;
    }

    let library: InsightPresetLibrary;
    if (preset.startsWith("saved:")) {
      const id = preset.slice("saved:".length);
      library = {
        ...presetLibrary,
        custom: presetLibrary.custom.map((saved) =>
          saved.id === id ? { ...saved, layers: [...layers] } : saved,
        ),
      };
    } else {
      const defaultId = preset as DefaultInsightPresetId;
      const defaults = { ...presetLibrary.defaults };
      if (sameLayers(layers, INSIGHT_PRESETS[defaultId].layers)) {
        delete defaults[defaultId];
      } else {
        defaults[defaultId] = [...layers];
      }
      library = { ...presetLibrary, defaults };
    }
    this.savePresetLibrary(library);
    setStorageKeyValue(ACTIVE_PRESET_KEY, preset);
    this.setState({ presetLibrary: library, presetDirty: false });
  }

  private openPresetDialog(dialog: Exclude<State["presetDialog"], null>) {
    const selected = this.selectedCustomPreset();
    this.setState({
      presetMenuAnchor: null,
      presetDialog: dialog,
      presetName: dialog === "rename" ? selected?.name || "" : "",
      presetNameError: "",
    });
  }

  private closePresetDialog() {
    this.setState({
      presetDialog: null,
      presetName: "",
      presetNameError: "",
    });
  }

  private validatedPresetName(excludeId?: string): string | null {
    const name = this.state.presetName.trim();
    if (!name) {
      this.setState({ presetNameError: "Enter a name." });
      return null;
    }
    const duplicateDefault = Object.values(INSIGHT_PRESETS).some(
      (preset) => preset.label.toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
    const duplicateCustom = this.state.presetLibrary.custom.some(
      (preset) =>
        preset.id !== excludeId &&
        preset.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
    if (duplicateDefault || duplicateCustom) {
      this.setState({ presetNameError: "That name is already in use." });
      return null;
    }
    return name;
  }

  private createCustomPreset() {
    if (
      !this.state.layers.length ||
      this.state.presetLibrary.custom.length >= MAX_CUSTOM_INSIGHT_PRESETS
    ) {
      return;
    }
    const name = this.validatedPresetName();
    if (!name) {
      return;
    }
    const id = nextCustomPresetId(this.state.presetLibrary.custom);
    const preset: CustomInsightPreset = {
      id,
      name,
      layers: [...this.state.layers],
    };
    const library = {
      ...this.state.presetLibrary,
      custom: [...this.state.presetLibrary.custom, preset],
    };
    const presetId = `saved:${id}` as const;
    this.savePresetLibrary(library);
    setStorageKeyValue(ACTIVE_PRESET_KEY, presetId);
    this.setState({
      presetLibrary: library,
      preset: presetId,
      presetDirty: false,
      presetDialog: null,
      presetName: "",
      presetNameError: "",
    });
  }

  private renameCustomPreset() {
    const selected = this.selectedCustomPreset();
    if (!selected) {
      return;
    }
    const name = this.validatedPresetName(selected.id);
    if (!name) {
      return;
    }
    const library = {
      ...this.state.presetLibrary,
      custom: this.state.presetLibrary.custom.map((preset) =>
        preset.id === selected.id ? { ...preset, name } : preset,
      ),
    };
    this.savePresetLibrary(library);
    this.setState({
      presetLibrary: library,
      presetDialog: null,
      presetName: "",
      presetNameError: "",
    });
  }

  private deleteCustomPreset() {
    const selected = this.selectedCustomPreset();
    if (!selected) {
      return;
    }
    const library = {
      ...this.state.presetLibrary,
      custom: this.state.presetLibrary.custom.filter(
        (preset) => preset.id !== selected.id,
      ),
    };
    this.savePresetLibrary(library);
    setStorageKeyValue(ACTIVE_PRESET_KEY, "custom");
    this.setState({
      presetLibrary: library,
      preset: "custom",
      presetDirty: true,
      presetDialog: null,
    });
  }

  private restoreDefaultPreset() {
    const { preset, presetLibrary } = this.state;
    if (preset === "custom" || preset.startsWith("saved:")) {
      return;
    }
    const defaultId = preset as DefaultInsightPresetId;
    const defaults = { ...presetLibrary.defaults };
    delete defaults[defaultId];
    const library = { ...presetLibrary, defaults };
    const layers = [...INSIGHT_PRESETS[defaultId].layers];
    this.savePresetLibrary(library);
    setStorageKeyValue(LAYERS_KEY, layers);
    setStorageKeyValue(ACTIVE_PRESET_KEY, preset);
    this.setState({
      presetLibrary: library,
      temporaryLayer: undefined,
      layers,
      presetDirty: false,
      presetDialog: null,
    });
  }

  private getProjection(now: TickPresentFutureType): ProjectionView {
    const { game } = this.props;
    const projectionStepMinutes = 60;
    const tickScale = projectionStepMinutes / TICK_MINUTES;
    const ticks = (TICKS_PER_YEAR * MAX_FORECAST_YEARS) / tickScale;
    const monthsAhead = MAX_FORECAST_YEARS * 12;
    const key = [
      game.date.monthsElapsed,
      game.monthlyHistory.length,
      game.dollarsPerkWh,
      game.feePerKgCO2e,
      facilitySignature(game),
      policySignature(game),
    ].join("|");
    if (this.projectionCache?.key === key) {
      return this.projectionCache.projection;
    }

    const timeline = generateNewTimeline(
      game,
      now.cash,
      now.customers,
      ticks,
      projectionStepMinutes,
    );
    const historicalSupplyDemand = [...game.monthlyHistory]
      .reverse()
      .map((month) => {
        const tick = { ...now } as TickPresentFutureType;
        tick.minute = monthMinute(month, game.startingYear);
        tick.supplyW = month.supplyWh / HOURS_PER_RECORDED_MONTH;
        tick.demandW = month.demandWh / HOURS_PER_RECORDED_MONTH;
        return tick;
      });
    const historicalCharts = [...game.monthlyHistory]
      .reverse()
      .flatMap((month) =>
        month.chartAverage
          ? [
              {
                ...month.chartAverage,
                minute: monthMinute(month, game.startingYear),
              } as TickPresentFutureType,
            ]
          : [],
      );
    let domainMin = Number.POSITIVE_INFINITY;
    let domainMax = 0;
    for (const tick of [...historicalSupplyDemand, ...timeline]) {
      domainMin = Math.min(domainMin, tick.supplyW, tick.demandW);
      domainMax = Math.max(domainMax, tick.supplyW, tick.demandW);
    }
    const [rangeMin, rangeMax] = viewportBounds(game);

    const { blackouts, blackoutTotalWh, largestBlackout } = forecastShortfalls(
      timeline,
      projectionStepMinutes,
      domainMax,
    );
    blackouts.unshift({ minute: rangeMin, value: 0 });
    const sampled = sampleForecastTimeline(
      timeline,
      240 * MAX_FORECAST_YEARS,
      projectionStepMinutes,
    );

    const currentMonth = summarizeTimeline(game.timeline, game.startingYear);
    const projectedMonths = summarizeTimelineByMonth(
      timeline,
      game.startingYear,
    ).slice(1, 1 + monthsAhead);
    const projection: ProjectionView = {
      timeline: [...historicalCharts, ...timeline],
      sampled: [...historicalCharts, ...sampled],
      supplyDemandTimeline: [...historicalSupplyDemand, ...timeline],
      domain: { x: [rangeMin, rangeMax], y: [domainMin, domainMax] },
      blackouts,
      blackoutTotalWh,
      largestBlackout,
      hasStorage: [...historicalCharts, ...timeline].some(
        (tick) => tick.storedWh > 0,
      ),
      hasHydro:
        game.facilities.some((facility) => facility.fuel === "Hydro") ||
        historicalCharts.some((tick) => tick.hydroReservoirCapacityWh > 0),
      financePast: game.monthlyHistory,
      financeProjected: [currentMonth, ...projectedMonths],
      projectionStepMinutes,
    };
    this.projectionCache = { key, projection };
    return projection;
  }

  private available(layer: InsightLayerDefinition, projection: ProjectionView) {
    return (
      !layer.availability ||
      (layer.availability === "storage" && projection.hasStorage) ||
      (layer.availability === "hydro" && projection.hasHydro) ||
      (layer.availability === "transmission" &&
        transmissionAvailable(this.props.game.location) &&
        !!this.props.game.transmission?.lines.some(
          ({ yearsToBuildLeft }) => yearsToBuildLeft <= 0,
        ))
    );
  }

  private renderLevers(now: TickPresentFutureType) {
    const { game, onDelta } = this.props;
    const scenario =
      getScenario(game.scenarioId, game.customScenario) || SCENARIOS[0];
    const marketRate = getMarketRate(
      scenario.dollarsPerkWh,
      game.date,
      game.startingYear,
      game.seed,
    );
    const customerChange = projectCustomerChange({
      customers: now.customers,
      customerRate: now.customerRate || game.customerRate || game.dollarsPerkWh,
      currentRate: game.dollarsPerkWh,
      marketRateAt: (tick) =>
        getMarketRate(
          scenario.dollarsPerkWh,
          getDateFromMinute(
            game.date.minute + tick * TICK_MINUTES,
            game.startingYear,
          ),
          game.startingYear,
          game.seed,
        ),
      marketSizeAt: (tick) =>
        customerMarketSizeAt(
          game.customerMarketSize || now.customers * 2,
          game.date.minute + tick * TICK_MINUTES,
        ),
      ownership: scenario.ownership,
    });
    const max =
      scenario.ownership === "Investor"
        ? Math.max(0.05, Math.ceil(marketRate * 200) / 100, game.dollarsPerkWh)
        : 0.3;
    const marks =
      scenario.ownership === "Investor"
        ? [
            { value: 0, label: rateMarkLabel(0, "$0", "0¢") },
            {
              value: marketRate,
              label: rateMarkLabel(
                marketRate,
                `${formatMoneyConcise(marketRate)} market`,
                `market ${formatRateCompact(marketRate)}`,
              ),
            },
            {
              value: max,
              label: rateMarkLabel(
                max,
                formatMoneyConcise(max),
                formatRateCompact(max),
              ),
            },
          ]
        : RATE_MARKS;
    const formattedCustomerChange = formatCustomerChange(
      customerChange,
      now.customers,
    );
    const rateSummary =
      scenario.ownership === "Investor"
        ? `Rate ${formatMoneyConcise(game.dollarsPerkWh)} per kilowatt hour; market rate ${formatMoneyConcise(marketRate)}; projected customers ${formattedCustomerChange} next month.`
        : `Rate ${formatMoneyConcise(game.dollarsPerkWh)} per kilowatt hour; market benchmark ${formatMoneyConcise(marketRate)}; customer growth plus ${(ORGANIC_GROWTH_MAX_ANNUAL * 100).toFixed(1)} percent per year. Public utility customers do not switch based on rates.`;
    return (
      <section className="insightsLevers" aria-label="Planning controls">
        <Button
          className="insightsRateToggle"
          startIcon={<TuneIcon />}
          onClick={() => this.setState({ leversOpen: !this.state.leversOpen })}
          aria-expanded={this.state.leversOpen}
          aria-controls="rateSliderControl"
          aria-label={`${this.state.leversOpen ? "Hide" : "Show"} rate slider`}
          aria-describedby="insightsRateSummary"
        >
          <span className="insightsRateToggleLabel">Rate controls</span>
        </Button>
        <CustomerPrograms
          game={game}
          onViewDemand={() => this.setLayers(["demandByType"])}
        />
        <Typography
          className="insightsRateSummaryDesktop"
          variant="body2"
          color="textSecondary"
          aria-hidden="true"
        >
          Base rate{" "}
          <strong>{formatMoneyConcise(game.dollarsPerkWh)}/kWh</strong>
          {scenario.ownership === "Investor" && (
            <>
              {" "}
              · market {formatMoneyConcise(marketRate)} · projected customers{" "}
              <strong>{formattedCustomerChange}</strong> next month
            </>
          )}
          {scenario.ownership === "Public" && (
            <>
              {" "}
              · market benchmark {formatMoneyConcise(marketRate)} · customer
              growth{" "}
              <strong>
                +{(ORGANIC_GROWTH_MAX_ANNUAL * 100).toFixed(1)}%/yr
              </strong>
            </>
          )}
        </Typography>
        <div
          className={`insightsRateMetrics ${
            scenario.ownership === "Public" ? "insightsRateMetricsPublic" : ""
          }`}
          aria-hidden="true"
        >
          <span className="insightsRateMetric">
            <span className="insightsRateMetricLabel">Base rate</span>
            <strong className="insightsRateMetricValue">
              {formatRateCompact(game.dollarsPerkWh)}/kWh
            </strong>
          </span>
          <span className="insightsRateMetric">
            <span className="insightsRateMetricLabel">Market</span>
            <span className="insightsRateMetricValue">
              {formatRateCompact(marketRate)}
            </span>
          </span>
          {scenario.ownership === "Investor" ? (
            <>
              <span className="insightsRateMetric">
                <span className="insightsRateMetricLabel">Customers / mo</span>
                <strong className="insightsRateMetricValue">
                  {formattedCustomerChange}
                </strong>
              </span>
            </>
          ) : (
            <span className="insightsRateMetric insightsRateMetricGrowth">
              <span className="insightsRateMetricLabel">
                Customer growth / yr
              </span>
              <strong className="insightsRateMetricValue">
                +{(ORGANIC_GROWTH_MAX_ANNUAL * 100).toFixed(1)}%
              </strong>
            </span>
          )}
        </div>
        <span id="insightsRateSummary" className="srOnly">
          {rateSummary}
        </span>
        {(game.policies?.programs.timeOfUse?.tier !== "Off" ||
          game.policies?.programs.curtailment?.tier !== "Off") &&
          game.policies && (
            <Typography variant="caption">
              Tariffs and credits adjust this base rate. Customer estimates here
              assume a flat rate; use Customer programs for demand and cash with
              offers.
            </Typography>
          )}
        <div
          className={`budgetSlider flex-newline ${
            this.state.leversOpen ? "" : "insightsRateSliderCollapsed"
          }`}
          id="rateSliderControl"
          data-testid="rate-slider-control"
        >
          <Slider
            // Keep the thumb local while it is moving. Dispatching every pointer step makes
            // the entire workbench reconcile and regenerate its long-range projection before
            // the browser can paint the next thumb position. The committed rate remounts this
            // uncontrolled slider through its key, so external changes still stay in sync.
            key={game.dollarsPerkWh}
            id="rateSlider"
            disabled={!!game.replayPlayback}
            defaultValue={game.dollarsPerkWh}
            aria-label="The rate you charge for electricity generation"
            valueLabelDisplay="auto"
            valueLabelFormat={(rate) => `${formatMoneyConcise(rate)}/kWh`}
            getAriaValueText={(rate) =>
              `${formatMoneyConcise(rate)} per kilowatt hour`
            }
            marks={marks}
            min={0}
            step={scenario.ownership === "Investor" ? 0.001 : 0.01}
            max={max}
            onChangeCommitted={(_event, value) =>
              onDelta({
                dollarsPerkWh: Array.isArray(value) ? value[0] : value,
              })
            }
          />
        </div>
      </section>
    );
  }

  private renderLayerPanel(projection: ProjectionView) {
    if (!this.state.layersOpen) {
      return null;
    }
    const required = requiredTutorialLayers(this.props.game.scenarioId);
    return (
      <section
        id="insightsLayerPanel"
        className="insightsLayerPanel"
        aria-label="Data layers"
      >
        {GROUPS.map((group) => {
          const layers = INSIGHT_LAYERS.filter(
            (layer) =>
              layer.group === group && this.available(layer, projection),
          );
          return (
            <div className="insightsLayerGroup" key={group}>
              <Typography variant="subtitle2">{group}</Typography>
              {layers.map((layer) => (
                <FormControlLabel
                  key={layer.id}
                  control={
                    <Checkbox
                      id={`insightsLayer${layer.id[0].toUpperCase()}${layer.id.slice(1)}`}
                      checked={this.state.layers.includes(layer.id)}
                      disabled={required.includes(layer.id)}
                      onChange={() => this.toggleLayer(layer.id)}
                    />
                  }
                  label={layer.label}
                />
              ))}
            </div>
          );
        })}
      </section>
    );
  }

  private renderFinanceDetails(projection: ProjectionView) {
    const { game, selectedFacilityId } = this.props;
    const summaryMonths = [
      ...[...projection.financePast].reverse(),
      ...projection.financeProjected,
    ];
    const summary = deriveExpandedSummary(
      summaryMonths.reduce(reduceHistories, { ...EMPTY_HISTORY }),
    );
    const units = this.context as UnitSystemType;
    const selected = game.facilities.find(
      (facility) => facility.id === selectedFacilityId,
    );
    const lifetime =
      selected && facilityLifetime(selected as FacilityOperatingType);
    return (
      <>
        {selected && lifetime && (
          <div className="selectedFacilitySummary">
            <strong>{selected.name}</strong>: {formatWattHours(lifetime.wh)}{" "}
            delivered · {formatMoneyConcise(lifetime.profit)} profit
          </div>
        )}
        <Table size="small" className="insightsSummaryTable">
          <TableBody>
            {Object.entries(buildChartKeys(units)).map(([key, metadata]) => {
              const value =
                key === "interestRate"
                  ? getTimeFromTimeline(game.date.minute, game.timeline)!
                      .interestRate
                  : summary[key as DerivedHistoryKeysType];
              return (
                <TableRow key={key}>
                  <TableCell sx={{ pl: 2 + (metadata.nesting || 0) * 2 }}>
                    {key === "interestRate"
                      ? "Current interest rate"
                      : metadata.label}
                  </TableCell>
                  <TableCell align="right">
                    {(metadata.formatTable || metadata.format)(value)}
                    {metadata.suffix ? " " + metadata.suffix : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }

  private setViewport(
    bounds: ChartViewportRange,
    minSpan: number,
    next: ChartViewportRange,
    announce = true,
  ) {
    const clamped = clampChartViewport(bounds, next, minSpan);
    this.setState({
      viewport: clamped,
      viewportAnnouncement: announce
        ? viewportAnnouncement(clamped, this.props.game.startingYear)
        : this.state.viewportAnnouncement,
    });
  }

  private renderViewportControls(
    bounds: ChartViewportRange,
    range: ChartViewportRange,
    minSpan: number,
  ) {
    const full = rangesEqual(bounds, range);
    const span = range[1] - range[0];
    const rangeLabel = viewportLabel(range, this.props.game.startingYear);
    const setRange = (next: ChartViewportRange) =>
      this.setViewport(bounds, minSpan, next);
    const iconButton = (
      label: string,
      icon: React.ReactNode,
      disabled: boolean,
      onClick: () => void,
    ) => (
      <Tooltip title={label}>
        <span className="insightsViewportButton">
          <IconButton
            size="small"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
    return (
      <section
        className="insightsViewportToolbar"
        aria-label="Chart time navigation"
        aria-describedby="insightsViewportHint"
      >
        <Typography
          className="insightsViewportDate"
          variant="body2"
          aria-label={`Displayed date range: ${rangeLabel}`}
        >
          {rangeLabel}
        </Typography>
        <div className="insightsViewportButtons">
          {iconButton(
            "Pan earlier",
            <ChevronLeftIcon />,
            full || range[0] <= bounds[0],
            () =>
              setRange(
                panChartViewport(
                  bounds,
                  range,
                  minSpan,
                  -span * VIEWPORT_PAN_FRACTION,
                ),
              ),
          )}
          {iconButton("Zoom out", <ZoomOutIcon />, full, () =>
            setRange(
              zoomChartViewport(
                bounds,
                range,
                minSpan,
                1 / VIEWPORT_ZOOM_FACTOR,
              ),
            ),
          )}
          {iconButton("Fit full timeline", <FitScreenIcon />, full, () =>
            setRange(bounds),
          )}
          {iconButton("Zoom in", <ZoomInIcon />, span <= minSpan, () =>
            setRange(
              zoomChartViewport(bounds, range, minSpan, VIEWPORT_ZOOM_FACTOR),
            ),
          )}
          {iconButton(
            "Pan later",
            <ChevronRightIcon />,
            full || range[1] >= bounds[1],
            () =>
              setRange(
                panChartViewport(
                  bounds,
                  range,
                  minSpan,
                  span * VIEWPORT_PAN_FRACTION,
                ),
              ),
          )}
        </div>
        <span id="insightsViewportHint" className="srOnly">
          Pinch or use the zoom controls to zoom. Swipe, drag, or use the pan
          controls to move through time.
        </span>
        <span className="srOnly" aria-live="polite">
          {this.state.viewportAnnouncement}
        </span>
      </section>
    );
  }

  private renderTrack(
    id: InsightLayerId,
    index: number,
    visible: InsightLayerId[],
    projection: ProjectionView,
  ) {
    const { game, selectedFacilityId } = this.props;
    const definition = INSIGHT_LAYERS.find((layer) => layer.id === id)!;
    const configured = this.state.layers.includes(id);
    const previousConfigured = visible
      .slice(0, index)
      .reverse()
      .find((layer) => this.state.layers.includes(layer));
    const nextConfigured = visible
      .slice(index + 1)
      .find((layer) => this.state.layers.includes(layer));
    const multiyear =
      projection.domain.x[1] - projection.domain.x[0] > 12 * MINUTES_PER_MONTH;
    const fuels = forecastFuels(
      getDispatchOrderedFuels(game.facilities) as FuelNameType[],
      projection.sampled,
    );
    const selected = game.facilities.find(
      (facility) => facility.id === selectedFacilityId,
    ) as Partial<GeneratorOperatingType> | undefined;
    const highlightFuel =
      selected?.fuel && fuels.includes(selected.fuel)
        ? selected.fuel
        : undefined;
    const finance = financeMetadata(id, this.context as UnitSystemType);
    const chartId = `chartInsights${id[0].toUpperCase()}${id.slice(1)}Plot`;

    let body: React.ReactNode;
    if (finance) {
      body = (
        <>
          <ChartFinances
            hideTitle
            id={chartId}
            height={140}
            timeline={financeSeries(
              finance.key,
              projection.financePast,
              projection.financeProjected,
              projection.domain.x,
              game.startingYear,
            )}
            title={finance.label}
            format={finance.format}
            startingYear={game.startingYear}
            domain={projection.domain.x}
            syncKey={SYNC_KEY}
          />
          {id === "emissions" && (
            <Typography variant="caption" color="textSecondary" component="p">
              Total includes local plants and estimated emissions from purchased
              electricity. Last completed month:{" "}
              {finance.format(game.monthlyHistory[0]?.localKgco2e || 0)} local +{" "}
              {finance.format(game.monthlyHistory[0]?.importedKgco2e || 0)}{" "}
              imported ({largeMassUnit(this.context as UnitSystemType)} CO2e).
              Neighboring-grid assumptions are in Interties.
            </Typography>
          )}
        </>
      );
    } else {
      switch (id) {
        case "inflationInterest":
          body = (
            <>
              {(["inflationRate", "interestRate"] as const).map((key) => (
                <ChartFinances
                  key={key}
                  id={chartId + key}
                  height={140}
                  timeline={financeSeries(
                    key,
                    projection.financePast,
                    projection.financeProjected,
                    projection.domain.x,
                    game.startingYear,
                  )}
                  title={
                    key === "inflationRate" ? "Inflation" : "Interest rate"
                  }
                  format={(value) => (value * 100).toFixed(2) + "%"}
                  startingYear={game.startingYear}
                  domain={projection.domain.x}
                  syncKey={SYNC_KEY}
                />
              ))}
            </>
          );
          break;
        case "supplyDemand":
          body = (
            <>
              <ChartForecastSupplyDemand
                height={140}
                timeline={projection.supplyDemandTimeline}
                blackouts={projection.blackouts}
                domain={projection.domain}
                startingYear={game.startingYear}
                multiyear={multiyear}
                syncKey={SYNC_KEY}
              />
              <Typography
                variant="caption"
                component="p"
                color="text.secondary"
                sx={{ mx: 2 }}
              >
                Supply is dispatched electricity. Reserve shows how much more
                demand the grid could cover within 15 minutes.
                <ManualLink
                  entry={MANUAL_ENTRY.RESERVE_CAPACITY}
                  text="How reserve works"
                />
              </Typography>
              {projection.blackoutTotalWh > 0 && (
                <Typography className="insightsWarning" variant="body2">
                  Forecasted shortfall: ~
                  {formatWattHours(projection.blackoutTotalWh)} of demand not
                  met · largest shortage{" "}
                  {formatWatts(projection.largestBlackout.peakW)}
                </Typography>
              )}
            </>
          );
          break;
        case "powerExchange":
          body = (
            <PowerExchangeSummary
              game={game}
              now={getTimeFromTimeline(game.date.minute, game.timeline)!}
            />
          );
          break;
        case "demandByType": {
          const demandTypes = demandTypesBySizeAtStart(
            projection.sampled,
            projection.domain.x[0],
          );
          const demandTypeLabels = Object.fromEntries(
            game.loadAdditions.map((addition) => [
              addition.demandType,
              addition.label,
            ]),
          );
          body = (
            <>
              <ChartLegend
                items={demandTypes.map((type) => ({
                  name: demandTypeLabels[type] || type,
                  color: demandTypeColors()[type],
                }))}
              />
              <ChartForecastDemandByType
                height={140}
                timeline={projection.sampled}
                domain={{ x: projection.domain.x }}
                displayTypes={demandTypes}
                typeLabels={demandTypeLabels}
                startingYear={game.startingYear}
                multiyear={multiyear}
                syncKey={SYNC_KEY}
              />
            </>
          );
          break;
        }
        case "supplyByFuel":
          body = (
            <>
              <ChartLegend
                items={[
                  ...[...fuels].reverse().map((fuel) => ({
                    name: fuel,
                    color: fuelColors()[fuel],
                    muted: !!highlightFuel && fuel !== highlightFuel,
                  })),
                  { name: "Demand", color: "", rule: true },
                ]}
              />
              <ChartForecastSupplyByFuel
                height={140}
                timeline={projection.sampled}
                domain={{ x: projection.domain.x }}
                startingYear={game.startingYear}
                multiyear={multiyear}
                fuels={fuels}
                syncKey={SYNC_KEY}
                highlightFuel={highlightFuel}
              />
            </>
          );
          break;
        case "storage":
          body = (
            <ChartForecastStorage
              height={140}
              timeline={projection.sampled}
              domain={{ x: projection.domain.x }}
              startingYear={game.startingYear}
              multiyear={multiyear}
              syncKey={SYNC_KEY}
            />
          );
          break;
        case "fuelPrices":
          body = (
            <>
              <ChartLegend
                items={PRICED_FUELS.map((fuel) => ({
                  name: fuel,
                  color: fuelColors()[fuel],
                  dash: fuelDashArrays[fuel],
                }))}
              />
              <ChartForecastFuelPrices
                height={140}
                timeline={projection.sampled}
                domain={{ x: projection.domain.x }}
                startingYear={game.startingYear}
                multiyear={multiyear}
                syncKey={SYNC_KEY}
              />
              <EconomicFutureComparison game={game} />
            </>
          );
          break;
        case "solarCapacityFactor":
          body = (
            <ChartForecastRenewableCapacityFactor
              game={game}
              height={140}
              timeline={projection.timeline}
              domain={{ x: projection.domain.x }}
              startingYear={game.startingYear}
              multiyear={multiyear}
              syncKey={SYNC_KEY}
            />
          );
          break;
        case "water":
          body = (
            <>
              <ChartLegend
                items={[
                  {
                    name: "Precipitation",
                    color: chartPalette().precipitation,
                  },
                  { name: "Snowpack", color: chartPalette().snowpack },
                  { name: "Reservoir", color: chartPalette().reservoir },
                ]}
              />
              <ChartForecastWater
                height={140}
                timeline={projection.sampled}
                domain={{ x: projection.domain.x }}
                startingYear={game.startingYear}
                multiyear={multiyear}
                syncKey={SYNC_KEY}
              />
            </>
          );
          break;
        case "weather":
          body = (
            <ChartForecastWeather
              height={140}
              timeline={projection.timeline}
              domain={{ x: projection.domain.x }}
              startingYear={game.startingYear}
              multiyear={multiyear}
              syncKey={SYNC_KEY}
            />
          );
          break;
        case "financeDetails":
          body = this.renderFinanceDetails(projection);
          break;
        default:
          body = null;
      }
    }

    // Stable wrappers keep existing walkthrough/deep-link targets while the canvas itself gets
    // a unique id per financial layer.
    if (id === "profit") {
      body = <div id="chartFinances">{body}</div>;
    } else if (id === "customers") {
      body = <div id="chartInsightsCustomers">{body}</div>;
    }

    return (
      <section
        className="insightsTrack"
        key={id}
        data-layer={id}
        tabIndex={-1}
        aria-label={`${definition.label} evidence`}
      >
        <Toolbar className="insightsTrackHeader">
          <Typography variant="h6">{definition.label}</Typography>
          <span className="insightsTrackActions">
            <IconButton
              size="small"
              aria-label={`Move ${definition.label} up`}
              disabled={!configured || !previousConfigured}
              onClick={() =>
                previousConfigured && this.moveLayer(id, previousConfigured)
              }
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Move ${definition.label} down`}
              disabled={!configured || !nextConfigured}
              onClick={() =>
                nextConfigured && this.moveLayer(id, nextConfigured)
              }
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={
                this.state.temporaryLayer === id &&
                !this.state.layers.includes(id)
                  ? `Keep ${definition.label}`
                  : `Remove ${definition.label}`
              }
              disabled={requiredTutorialLayers(game.scenarioId).includes(id)}
              onClick={() => this.toggleLayer(id)}
            >
              {this.state.temporaryLayer === id && !configured ? (
                <AddIcon fontSize="small" />
              ) : (
                <CloseIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Toolbar>
        {this.state.temporaryLayer === id &&
          !this.state.layers.includes(id) && (
            <div className="temporaryEvidence">
              Temporary evidence · not saved with preset
            </div>
          )}
        {body}
        {id === "supplyDemand" && (
          <Button
            id="insightsGeneratorJourney"
            onClick={() => this.beginJourney("supplyDemand")}
            sx={{ minHeight: 44, mx: 1, mb: 1 }}
          >
            Generator
          </Button>
        )}
      </section>
    );
  }

  private renderPresetDialogs() {
    const { presetDialog, presetName, presetNameError, presetLibrary } =
      this.state;
    if (!presetDialog) {
      return <></>;
    }
    const selected =
      presetDefinition(this.state.preset, presetLibrary)?.label || "preset";
    const naming = presetDialog === "saveAs" || presetDialog === "rename";
    const title =
      presetDialog === "saveAs"
        ? "Save as a new preset"
        : presetDialog === "rename"
          ? "Rename preset"
          : presetDialog === "delete"
            ? `Delete “${selected}”?`
            : `Restore “${selected}”?`;
    const description =
      presetDialog === "saveAs"
        ? `Save the current ${this.state.layers.length} charts and their order as a reusable preset.`
        : presetDialog === "rename"
          ? "Give this preset a short, recognizable name."
          : presetDialog === "delete"
            ? "The charts stay open as an unsaved view, but this named preset will be removed."
            : "This replaces your saved changes with the original charts and order. Your custom presets are not affected.";

    return (
      <Dialog
        className="insightsPresetDialog"
        open={presetDialog !== null}
        onClose={() => this.closePresetDialog()}
        fullWidth
        maxWidth="xs"
        aria-labelledby="insight-preset-dialog-title"
        aria-describedby="insight-preset-dialog-description"
      >
        <DialogTitle id="insight-preset-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="insight-preset-dialog-description">
            {description}
          </DialogContentText>
          {naming ? (
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Preset name"
              value={presetName}
              error={!!presetNameError}
              helperText={
                presetNameError ||
                (presetDialog === "saveAs"
                  ? `${presetName.length}/${MAX_PRESET_NAME_LENGTH} characters · ${presetLibrary.custom.length}/${MAX_CUSTOM_INSIGHT_PRESETS} custom presets`
                  : `${presetName.length}/${MAX_PRESET_NAME_LENGTH} characters`)
              }
              slotProps={{ htmlInput: { maxLength: MAX_PRESET_NAME_LENGTH } }}
              onChange={(event) =>
                this.setState({
                  presetName: event.target.value,
                  presetNameError: "",
                })
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  if (presetDialog === "saveAs") {
                    this.createCustomPreset();
                  } else {
                    this.renameCustomPreset();
                  }
                }
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.closePresetDialog()}>Cancel</Button>
          <Button
            variant="contained"
            color={presetDialog === "delete" ? "error" : "primary"}
            onClick={() => {
              if (presetDialog === "saveAs") {
                this.createCustomPreset();
              } else if (presetDialog === "rename") {
                this.renameCustomPreset();
              } else if (presetDialog === "delete") {
                this.deleteCustomPreset();
              } else {
                this.restoreDefaultPreset();
              }
            }}
          >
            {presetDialog === "saveAs"
              ? "Save preset"
              : presetDialog === "rename"
                ? "Rename"
                : presetDialog === "delete"
                  ? "Delete"
                  : "Restore"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  public render() {
    const { game } = this.props;
    const now = getTimeFromTimeline(game.date.minute, game.timeline);
    if (!now) {
      return <span />;
    }
    const projection = this.getProjection(now);
    const viewportBounds = projection.domain.x;
    const minViewportSpan = Math.min(
      viewportBounds[1] - viewportBounds[0],
      MINUTES_PER_MONTH,
    );
    const viewportRange = clampChartViewport(
      viewportBounds,
      this.state.viewport,
      minViewportSpan,
    );
    const viewportTimeline = timelineWithin(projection.timeline, viewportRange);
    const viewportSupplyDemand = timelineWithin(
      projection.supplyDemandTimeline,
      viewportRange,
    );
    const viewportSampleInterval = Math.max(
      projection.projectionStepMinutes,
      Math.ceil(
        (viewportRange[1] - viewportRange[0]) /
          1600 /
          projection.projectionStepMinutes,
      ) * projection.projectionStepMinutes,
    );
    const sampled = sampleForecastTimeline(
      viewportTimeline,
      viewportSampleInterval,
      projection.projectionStepMinutes,
    );
    const supplyDemandTimeline = sampleForecastTimeline(
      viewportSupplyDemand,
      viewportSampleInterval,
      projection.projectionStepMinutes,
    );
    let viewportDomainMin = Number.POSITIVE_INFINITY;
    let viewportDomainMax = Number.NEGATIVE_INFINITY;
    for (const tick of supplyDemandTimeline) {
      viewportDomainMin = Math.min(
        viewportDomainMin,
        tick.supplyW,
        tick.demandW,
      );
      viewportDomainMax = Math.max(
        viewportDomainMax,
        tick.supplyW,
        tick.demandW,
      );
    }
    const viewportProjection: ProjectionView = {
      ...projection,
      domain: {
        x: viewportRange,
        y:
          Number.isFinite(viewportDomainMin) &&
          Number.isFinite(viewportDomainMax)
            ? [viewportDomainMin, viewportDomainMax]
            : projection.domain.y,
      },
      timeline: viewportTimeline,
      sampled,
      supplyDemandTimeline,
    };
    const viewportContext = {
      bounds: viewportBounds,
      range: viewportRange,
      minSpan: minViewportSpan,
      onRangeChange: (range: ChartViewportRange, announce = false) =>
        this.setViewport(viewportBounds, minViewportSpan, range, announce),
      onReset: (announce = false) =>
        this.setViewport(
          viewportBounds,
          minViewportSpan,
          viewportBounds,
          announce,
        ),
    };
    const visible = withRequiredLayers(
      this.state.temporaryLayer &&
        !this.state.layers.includes(this.state.temporaryLayer)
        ? [...this.state.layers, this.state.temporaryLayer]
        : this.state.layers,
      game.scenarioId,
    ).filter((id) => {
      const definition = INSIGHT_LAYERS.find((layer) => layer.id === id);
      return !!definition && this.available(definition, projection);
    });
    const selectedPreset = presetDefinition(
      this.state.preset,
      this.state.presetLibrary,
    );
    const selectedCustom = this.selectedCustomPreset();
    const selectedDefault =
      this.state.preset !== "custom" && !this.state.preset.startsWith("saved:")
        ? (this.state.preset as DefaultInsightPresetId)
        : null;
    const customLimitReached =
      this.state.presetLibrary.custom.length >= MAX_CUSTOM_INSIGHT_PRESETS;
    const selectedDefaultCustomized =
      !!selectedDefault &&
      !!this.state.presetLibrary.defaults[selectedDefault] &&
      !this.state.presetDirty;
    const upcomingEvents = (this.props.upcomingEvents || []).filter(
      (event) =>
        event.startsMinute !== undefined &&
        event.startsMinute >= viewportRange[0] &&
        event.startsMinute <= viewportRange[1],
    );
    const annotations = {
      events: upcomingEvents.map((event, index) => ({
        key: event.key,
        x: event.startsMinute!,
        number: index + 1,
      })),
      activeEventKey: this.state.activeEventKey,
    };

    return (
      <GameCard className="insights" id="insightsPane">
        <div className="scrollable">
          <Toolbar className="paneHeader insightsHeader">
            <Typography variant="h6">Insights</Typography>
            <div className="insightsHeaderControls">
              <div
                className="insightsPresetControls"
                role="group"
                aria-label="Preset controls"
              >
                <Select
                  id="insightsPreset"
                  value={this.state.preset}
                  onChange={(event: SelectChangeEvent<InsightPresetId>) => {
                    const preset = event.target.value as InsightPresetId;
                    if (preset !== "custom") {
                      this.applyPreset(preset);
                    }
                  }}
                  className="headerControl insightsPreset"
                  aria-label="Insight preset"
                  renderValue={() => (
                    <span className="insightsPresetValue">
                      <span className="insightsPresetName">
                        {selectedPreset?.label || "Unsaved view"}
                      </span>
                      {this.state.presetDirty && selectedPreset && (
                        <span
                          className="insightsPresetEdited"
                          role="status"
                          aria-label="Unsaved changes"
                        >
                          Edited
                        </span>
                      )}
                      {selectedDefaultCustomized && (
                        <span className="insightsPresetCustomized">
                          Customized
                        </span>
                      )}
                    </span>
                  )}
                >
                  <ListSubheader>Default presets</ListSubheader>
                  {Object.entries(INSIGHT_PRESETS).map(([id, preset]) => {
                    const modified =
                      !!this.state.presetLibrary.defaults[
                        id as DefaultInsightPresetId
                      ];
                    return (
                      <MenuItem key={id} value={id} data-insight-preset={id}>
                        <span>{preset.label}</span>
                        {modified && (
                          <span className="insightsPresetMenuHint">
                            Modified
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                  {!!this.state.presetLibrary.custom.length && (
                    <ListSubheader>
                      Your presets ({this.state.presetLibrary.custom.length}/
                      {MAX_CUSTOM_INSIGHT_PRESETS})
                    </ListSubheader>
                  )}
                  {this.state.presetLibrary.custom.map((preset) => (
                    <MenuItem
                      key={preset.id}
                      value={`saved:${preset.id}`}
                      data-insight-preset={preset.id}
                    >
                      <span className="insightsPresetMenuName">
                        {preset.name}
                      </span>
                    </MenuItem>
                  ))}
                  <MenuItem value="custom" disabled>
                    Unsaved view
                  </MenuItem>
                </Select>
                <IconButton
                  className="insightsPresetSave"
                  size="small"
                  aria-label={
                    this.state.preset === "custom" ? "Save as" : "Save"
                  }
                  disabled={
                    !this.state.layers.length ||
                    (this.state.preset !== "custom" &&
                      !this.state.presetDirty) ||
                    (this.state.preset === "custom" && customLimitReached)
                  }
                  title={
                    this.state.preset === "custom" && customLimitReached
                      ? `Limit of ${MAX_CUSTOM_INSIGHT_PRESETS} custom presets reached`
                      : undefined
                  }
                  onClick={() => this.savePresetChanges()}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
                <Tooltip title="Rename, save a copy, restore, or delete">
                  <IconButton
                    className="insightsPresetActions"
                    size="small"
                    aria-label="Preset actions"
                    aria-haspopup="menu"
                    aria-controls={
                      this.state.presetMenuAnchor
                        ? "insightsPresetActionsMenu"
                        : undefined
                    }
                    aria-expanded={!!this.state.presetMenuAnchor}
                    onClick={(event) =>
                      this.setState({ presetMenuAnchor: event.currentTarget })
                    }
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <Tooltip title={`Choose layers (${visible.length} shown)`}>
                <Button
                  id="insightsLayersButton"
                  className="insightsLayerControls"
                  size="small"
                  onClick={() =>
                    this.setState({ layersOpen: !this.state.layersOpen })
                  }
                  aria-label={
                    this.state.layersOpen
                      ? "Done choosing layers"
                      : `Layers (${visible.length} shown)`
                  }
                  aria-expanded={this.state.layersOpen}
                  aria-controls="insightsLayerPanel"
                >
                  {this.state.layersOpen ? "Done" : "Layers"}
                </Button>
              </Tooltip>
            </div>
            <Menu
              id="insightsPresetActionsMenu"
              anchorEl={this.state.presetMenuAnchor}
              open={!!this.state.presetMenuAnchor}
              onClose={() => this.setState({ presetMenuAnchor: null })}
            >
              <MenuItem
                className="insightsPresetSaveMenuItem"
                disabled={
                  !this.state.layers.length ||
                  (this.state.preset !== "custom" && !this.state.presetDirty) ||
                  (this.state.preset === "custom" && customLimitReached)
                }
                onClick={() => {
                  this.setState({ presetMenuAnchor: null }, () =>
                    this.savePresetChanges(),
                  );
                }}
              >
                <SaveIcon fontSize="small" />
                {this.state.preset === "custom"
                  ? "Save as new preset"
                  : "Save preset changes"}
              </MenuItem>
              <MenuItem
                className="insightsPresetSaveAsMenuItem"
                disabled={customLimitReached || !this.state.layers.length}
                onClick={() => this.openPresetDialog("saveAs")}
              >
                Save as new preset
              </MenuItem>
              {selectedCustom && (
                <MenuItem onClick={() => this.openPresetDialog("rename")}>
                  Rename preset…
                </MenuItem>
              )}
              {selectedCustom && (
                <MenuItem onClick={() => this.openPresetDialog("delete")}>
                  Delete preset…
                </MenuItem>
              )}
              {selectedDefault &&
                this.state.presetLibrary.defaults[selectedDefault] && (
                  <MenuItem onClick={() => this.openPresetDialog("restore")}>
                    Restore original preset
                  </MenuItem>
                )}
            </Menu>
          </Toolbar>
          <ForecastScope />
          {this.renderLayerPanel(projection)}
          {this.renderLevers(now)}
          {game.monthlyHistory.length > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ px: 2 }}>
              Past charts show monthly averages; financial charts show monthly
              totals or ending balances.
              {game.monthlyHistory.some((month) => !month.chartAverage) &&
                " Older saves have only financial and supply/demand history until new months are recorded."}
            </Typography>
          )}
          {this.renderViewportControls(
            viewportBounds,
            viewportRange,
            minViewportSpan,
          )}
          {!!upcomingEvents.length && (
            <InsightEventRail
              events={upcomingEvents}
              activeKey={this.state.activeEventKey}
              onActiveChange={(activeEventKey) =>
                this.setState({ activeEventKey })
              }
              onZoom={(event) =>
                this.setViewport(
                  viewportBounds,
                  minViewportSpan,
                  eventChartViewport(
                    viewportBounds,
                    event.startsMinute!,
                    event.endsMinute,
                    minViewportSpan,
                  ),
                )
              }
            />
          )}
          <ChartViewportContext.Provider value={viewportContext}>
            <ChartAnnotationsContext.Provider value={annotations}>
              <div className="insightsTracks">
                {visible.map((id, index) =>
                  this.renderTrack(id, index, visible, viewportProjection),
                )}
                {!visible.length && (
                  <Typography className="insightsEmpty" color="textSecondary">
                    Choose Layers to build this view.
                  </Typography>
                )}
              </div>
            </ChartAnnotationsContext.Provider>
          </ChartViewportContext.Provider>
        </div>
        {this.renderPresetDialogs()}
      </GameCard>
    );
  }
}
